<template>
  <!-- thumbnail area -->
  <div v-if="items.length > 1" class="lg:mx-auto overflow-hidden mt-4">
    <div
      ref="thumbnailsSlider"
      class="flex flex-nowrap -mx-2 transition-transform duration-500 ease-in-out"
    >
      <div
        v-for="(item, index) in items"
        :key="item.id"
        class="w-1/3 shrink-0 grow-0 px-2"
        :data-testid="`media-gallery-thumbnail-${index}`"
        @click="setCurrentSlide(index)"
      >
        <!-- thumbnail for videos -->
        <div
          v-if="item.component === 'media-gallery.video'"
          class="relative rounded-md border-2 cursor-pointer opacity-50 hover:opacity-100 transition-[border-color,opacity] duration-300 ease-in-out"
          :class="
            currentItemIndex === index
              ? 'border-brand opacity-100'
              : 'border-transparent'
          "
        >
          <strapi-image
            v-if="asGalleryVideo(item).thumbnail"
            class="rounded-md"
            :image="asGalleryVideo(item).thumbnail"
            :alt="asGalleryVideo(item).thumbnail?.alternativeText"
            fluid
          />
          <div v-else class="aspect-video w-full rounded-md bg-depth-2" />

          <fa-icon
            :icon="faCirclePlay"
            class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-brand size-8 md:size-10 lg:size-14"
            aria-hidden="true"
          />
        </div>

        <!-- thumbnail for images -->
        <strapi-image
          v-else-if="
            item.component === 'media-gallery.image' &&
            asGalleryImage(item).image
          "
          class="w-full rounded-md border-2 cursor-pointer opacity-50 hover:opacity-100 transition-[border-color,opacity] duration-300 ease-in-out"
          :class="
            currentItemIndex === index
              ? 'border-brand opacity-100'
              : 'border-transparent'
          "
          :image="getStrapiImageThumbnail(item)"
          :alt="asGalleryImage(item).image?.alternativeText"
          :alt-fallback="`Thumbnail ${asGalleryImage(item).image?.id}`"
          fluid
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { faCirclePlay } from '@fortawesome/pro-solid-svg-icons';
import type {
  StrapiImage,
  StrapiImageFormat,
  StrapiMediaGalleryItem,
} from '~/apollo/types/types';
import { useStrapiMediaTypes } from './features';

const props = withDefaults(
  defineProps<{
    currentItemIndex?: number;
    items: StrapiMediaGalleryItem[];
  }>(),
  {
    currentItemIndex: 0,
  },
);

const emit = defineEmits<{
  (e: 'thumbnail-clicked', index: number): void;
}>();

const thumbnailsSlider = ref<HTMLElement | null>(null);

const { asGalleryImage, asGalleryVideo } = useStrapiMediaTypes();

const setCurrentSlide = (index: number) => emit('thumbnail-clicked', index);

const getStrapiImageThumbnail = (
  item: StrapiMediaGalleryItem,
): StrapiImage | StrapiImageFormat | undefined => {
  const image = asGalleryImage(item);
  const thumbnail = image.image?.formats.find(
    (format) => format.format === 'thumbnail',
  );
  return thumbnail != null ? thumbnail : image.image;
};

const watchCurrentIndexHandler = watch(
  () => props.currentItemIndex,
  (currentItemIndex) => {
    if (!thumbnailsSlider.value) return;

    const totalItems = props.items.length;

    // If 3 or fewer items, no sliding needed — all visible
    if (totalItems <= 3) {
      thumbnailsSlider.value.style.transform = `translate3d(0, 0, 0)`;
      return;
    }

    // Always show exactly 3 thumbnails:
    // - First item: stays at start, show [0, 1, 2]
    // - Last item: stays at end, show [N-3, N-2, N-1]
    // - Middle items: centered, show [current-1, current, current+1]
    let slideOffset: number;

    if (currentItemIndex === 0) {
      slideOffset = 0;
    } else if (currentItemIndex === totalItems - 1) {
      slideOffset = (totalItems - 3) * 33.333333;
    } else {
      slideOffset = (currentItemIndex - 1) * 33.333333;
    }

    thumbnailsSlider.value.style.transform = `translate3d(-${slideOffset}%, 0, 0)`;
  },
  { immediate: true },
);

onUnmounted(() => {
  watchCurrentIndexHandler();
});
</script>
